import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  IEbayCategoriesResponse,
  IEbayStoreCategoryResponse,
  IEbayStoreCategoryRefreshResponse,
} from "./../models/EbayOffer";

export interface IUseEbayCategoriesOptions {
  /** Fetch the eBay (vehicle) categories. Turn off when the caller only needs store categories. */
  categories?: boolean;
  /** Fetch the stored shop categories. Turn off when the caller only needs eBay categories. */
  storeCategories?: boolean;
  /** Gate both fetches, e.g. on a modal's `open`, so an always-mounted caller fetches only when shown. */
  enabled?: boolean;
}

// Both lists are a live eBay call server-side and change rarely, so hold them for a full hour
// (cacheTime must match staleTime or the entry is dropped 5 minutes after the last subscriber
// unmounts) and don't re-read them just because the tab regained focus.
const ONE_HOUR = 1000 * 60 * 60;

export default function useEbayCategories({ categories: wantCategories = true, storeCategories: wantStoreCategories = true, enabled = true }: IUseEbayCategoriesOptions = {}) {
  const { get, post } = useApiHelper();
  const queryClient = useQueryClient();

  // const categories = useQuery({
  //   queryKey: ["ebayCategories", title],
  //   queryFn: () =>
  //     get<IEbayCategoriesResponse>(
  //       `/ebay/categories?${title ? `query=${title}` : ""}`
  //     ).then((res) => res.data),
  // });

  const categories = useQuery({
    queryKey: ["ebayCategories"],
    queryFn: () =>
      get<IEbayCategoriesResponse>(
        `/ebay/vehiclecategories`
      ).then((res) => res.data),
    enabled: enabled && wantCategories,
    staleTime: ONE_HOUR,
    cacheTime: ONE_HOUR,
    refetchOnWindowFocus: false,
  });

  // Served from our own table, not eBay, so the picker shows exactly what a publish will send. eBay
  // takes the category's path in an offer, not its id, so the path has to be stored to be resolvable.
  const storeCategories = useQuery({
    queryKey: ["ebayStoreCategories"],
    queryFn: () =>
      get<IEbayStoreCategoryResponse>(
        `/ebay/store/categories/stored`
      ).then((res) => res.data),
    enabled: enabled && wantStoreCategories,
    staleTime: ONE_HOUR,
    cacheTime: ONE_HOUR,
    refetchOnWindowFocus: false,
  });

  // Run after adding, renaming or moving a shop category on eBay. Nothing picks changes up on its own:
  // the list only changes when somebody edits the shop, and re-reading it per listing would be a
  // request per part on a bulk run.
  const refreshStoreCategories = useMutation({
    mutationFn: () =>
      post<{}, IEbayStoreCategoryRefreshResponse>(
        `/ebay/store/categories/refresh`,
        {}
      ).then((res) => res.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["ebayStoreCategories"] }),
  });

  return {
    categories,
    storeCategories,
    refreshStoreCategories
  };
}
